import React, { useState, useEffect } from "react";
import { getExercises, checkAnswer } from "../../api";
import MatrixGrid from "../matrixGrid/MatrixGrid";
import styles from "./ExerciseBoxes.module.css";
import { determinant, EigenvalueDecomposition, Matrix } from "ml-matrix";
import { OPERATION_TYPE } from "../../constants";

const INITIAL_DATA = [
    {
        "id": "d1ed1f08-5802-4909-8cc9-922c2e862761",
        "operationType": 4,
        "matrix1": {
            "columns": 3,
            "rows": 3,
            "values": [
                1.0,
                2.0,
                3.0,
                4.0,
                5.0,
                6.0,
                7.0,
                8.0,
                9.0
            ]
        },
        "matrix2": null,
        "userId": "asdgfs",
        "result_matrix": null,
        "determinant": 5.0,
        "eigenvalues": null,
        "eigenvectors": null
    },
    {
        "id": "cf9e6cec-5984-468d-ba36-8ebf12054c2e",
        "operationType": 5,
        "matrix1": {
            "columns": 3,
            "rows": 3,
            "values": [
                1.0,
                2.0,
                3.0,
                4.0,
                5.0,
                6.0,
                7.0,
                8.0,
                9.0
            ]
        },
        "matrix2": null,
        "userId": "asdgfs",
        "result_matrix": null,
        "determinant": null,
        "eigenvalues": [1,2,3],
        "eigenvectors": null
    },
    {
        "id": "ea2e9882-52d5-43af-958c-90039fab2f51",
        "operationType": 2,
        "matrix1": {
            "columns": 3,
            "rows": 3,
            "values": [
                1.0,
                2.0,
                3.0,
                4.0,
                5.0,
                6.0,
                7.0,
                8.0,
                9.0
            ]
        },
        "matrix2": {
            "columns": 3,
            "rows": 3,
            "values": [
                1.0,
                2.0,
                3.0,
                4.0,
                5.0,
                6.0,
                7.0,
                8.0,
                9.0
            ]
        },
        "userId": "asdgfs",
        "result_matrix": {
            "columns": 3,
            "rows": 3,
            "values": [
                1.0,
                2.0,
                3.0,
                4.0,
                5.0,
                6.0,
                7.0,
                8.0,
                9.0
            ]
        },
        "determinant": null,
        "eigenvalues": null,
        "eigenvectors": null
    }
];

const ExerciseBoxes = ({ isTeacher }) => {
    const [exercises, setExercises] = useState([]);
    const [detAnswer, setDetAnswer] = useState({});
    const [results, setResults] = useState({});
    const [inputs, setInputs] = useState({});

    useEffect(() => {
        getExercises()
        .then(data => setExercises(data))
        .catch(err => {
            console.log(err);
            setExercises(INITIAL_DATA);
        })
    }, []);


    useEffect(() => {
        checkAnswer(detAnswer)
            .then(data => {
                if (data.correct) handleResults(detAnswer.id, "Correct");
                else handleResults(detAnswer.id, "Incorrect");
            })
            .catch((err) => handleResults(detAnswer.id, "Incorrect"));
    }, [detAnswer]);

    function roundToThree(num) {
        return (Math.round((num + Number.EPSILON) * 1000) / 1000)
    }

    const handleInput = (id, val) => setInputs({...inputs, [id]: val})
    const handleResults = (id, val) => setResults({ ...results, [id]: val })

    const handleEigenVectorChange = (val, index, id) => {
        let newList = inputs[id] || [0, 0, 0, 0, 0, 0, 0, 0, 0];
        newList[index] = val
        handleInput(id, newList);
        setDetAnswer({
            'eigenvectors': newList,
            'id': id
        });
    };

    return (
        <div className={styles.box_wrapper}>
            <div className={styles.box_row}>
                <div className={[styles.purple_box, styles.box].join(' ')}>
                    <div className={styles.box_title}>
                        Matrix Addition & Subtraction & Multiplication
                    </div>
                    <div className={[styles.box_separator, styles.purple_separator].join(' ')} />
                    <div className={styles.matrices}>
                        {
                            exercises && exercises.filter(ex =>
                                ex.operationType === OPERATION_TYPE.ADDITION.getCode()
                                || ex.operationType === OPERATION_TYPE.SUBTRACTION.getCode()
                                || ex.operationType === OPERATION_TYPE.MULTIPLICATION.getCode())
                                .map((ex) => {
                                    const mt1 = ex.matrix1
                                    const mt2 = ex.matrix2
                                    const matrix1 = Matrix.from1DArray(mt1.rows, mt1.columns, mt1.values);
                                    const matrix2 = Matrix.from1DArray(mt2.rows, mt2.columns, mt2.values);
                                    if (!(ex.id in inputs))
                                        handleInput(ex.id, new Matrix(3, 3));

                                    return (
                                        <div>
                                            <div className={styles.matrix} key={mt1.id}>
                                                <MatrixGrid matrix={matrix1} editable={false} />
                                                <p>{ex.operationType === OPERATION_TYPE.ADDITION.getCode() ?
                                                '+' :
                                                    ex.operationType === OPERATION_TYPE.ADDITION.getCode() ?
                                                '-' :
                                                'x'}
                                                </p>
                                                <MatrixGrid matrix={matrix2} editable={false} />
                                                <p>=</p>
                                                <MatrixGrid
                                                    matrix={inputs[ex.id]}
                                                    setMatrix={f => {
                                                        const newMatrix = f(inputs[ex.id])
                                                        handleInput(ex.id, newMatrix)
                                                        setDetAnswer({
                                                            'result_matrix': {
                                                                'rows': 3,
                                                                'columns': 3,
                                                                'values': newMatrix.to1DArray()
                                                            },
                                                            'id': ex.id
                                                        })
                                                    }}
                                                    editable={true}
                                                />
                                            </div>
                                            <p> {results[ex.id]}</p>
                                        </div>
                                    );

                                })
                        }
                    </div>
                </div>
            </div>
            <div className={styles.box_row}>
                <div className={[styles.red_box, styles.box].join(' ')}>
                    <div className={styles.box_title}>
                        Determinant
                    </div>
                    <div className={[styles.box_separator, styles.purple_separator].join(' ')} />
                    <div className={styles.matrices}>
                        {
                            exercises && exercises.filter(ex => ex.operationType === OPERATION_TYPE.DETERMINANT.getCode())
                                .map((ex) => {
                                    const mt = ex.matrix1
                                    const matrix = Matrix.from1DArray(mt.rows, mt.columns, mt.values);
                                    return (
                                        <div className={styles.matrix} key={mt.id}>
                                            <MatrixGrid matrix={matrix} editable={false} />
                                            <input
                                                className={styles.inp_box}
                                                type="number"
                                                name="detAnswer"
                                                value={inputs[ex.id]}
                                                onChange={e => {
                                                    setDetAnswer({
                                                        'determinant': e.target.value,
                                                        'id': ex.id
                                                    })
                                                    handleInput(ex.id, e.target.value);
                                                }
                                                }
                                            />
                                            <p>{results[ex.id]}</p>
                                        </div>
                                    );
                                })
                        }
                    </div>
                </div>
            </div>
            <div className={styles.box_row}>
                <div className={[styles.orange_box, styles.box].join(' ')}>
                    <div className={styles.box_title}>
                        Eigenvalues & Eigenvectors
                    </div>
                    <div className={[styles.box_separator, styles.orange_separator].join(' ')} />
                    <div className={styles.matrices}>
                        {
                            exercises && exercises.filter(ex => ex.operationType == OPERATION_TYPE.EIGENVALUES.getCode() ||
                                ex.operationType == OPERATION_TYPE.EIGENVECTORS.getCode())
                                .map((ex) => {
                                    const mt = ex.matrix1
                                    const matrix = Matrix.from1DArray(mt.rows, mt.columns, mt.values);
                                    console.log(inputs)
                                    console.log(ex.operationType)
                                    console.log(OPERATION_TYPE.EIGENVALUES.getCode())
                                    if (!(ex.id in inputs) && ex.operationType == OPERATION_TYPE.EIGENVALUES.getCode())
                                        handleInput(ex.id, [0, 0, 0]);
                                    // if (!(ex.id in inputs) && ex.operationType === OPERATION_TYPE.EIGENVECTORS.getCode())
                                    //     handleInput(ex.id, [[0, 0, 0], [0, 0, 0], [0, 0, 0]]);
                                    console.log(inputs)
                                    console.log(ex.operationType)
                                    return (
                                        <div className={styles.matrix} key={mt.id}>
                                            <MatrixGrid matrix={matrix} editable={false} />
                                            {ex.operationType === OPERATION_TYPE.EIGENVALUES.getCode() && 
                                                <p>The eigenvalues are:
                                                    {[0, 1, 2].map(index =>
                                                        <p>λ<sub>{index}</sub> : <b>
                                                            {/* {roundToThree(eigenvalue)} */}
                                                            <input
                                                                className={styles.inp_box}
                                                                type="number"
                                                                name="detAnswer"
                                                                key={index}
                                                                value={inputs && inputs[ex.id] && inputs[ex.id][index]}
                                                                onChange={e => {
                                                                    let newList = inputs[ex.id] || [0, 0, 0];
                                                                    newList[index] = e.target.value
                                                                    handleInput(ex.id, newList);
                                                                    setDetAnswer({
                                                                        'eigenvalues': newList,
                                                                        'id': ex.id
                                                                    });
                                                                }}
                                                            />
                                                            </b></p>)}
                                                </p>
                                            }
                                            {ex.operationType === OPERATION_TYPE.EIGENVECTORS.getCode() &&
                                                <p>The eigenvectors are:
                                                    {[0, 1, 2].map(row =>
                                                        <p>
                                                            <input
                                                                className={styles.inp_box}
                                                                type="number"
                                                                name="detAnswer"
                                                                key={3*row + 0}
                                                                value={inputs && inputs[ex.id] && inputs[ex.id][3*row]}
                                                                onChange={e => handleEigenVectorChange(e.target.value, 3 * row, ex.id)}
                                                            />
                                                            <input
                                                                className={styles.inp_box}
                                                                type="number"
                                                                name="detAnswer"
                                                                key={3*row + 1}
                                                                value={inputs && inputs[ex.id] && inputs[ex.id][3 * row + 1]}
                                                                onChange={e => handleEigenVectorChange(e.target.value, 3 * row + 1, ex.id)}
                                                            /><input
                                                                className={styles.inp_box}
                                                                type="number"
                                                                name="detAnswer"
                                                                key={3*row + 2}
                                                                value={inputs && inputs[ex.id] && inputs[ex.id][3 * row + 2]}
                                                                onChange={e => handleEigenVectorChange(e.target.value, 3 * row + 2, ex.id)}
                                                            />
                                                        </p>)}
                                                </p>
                                            }
                                            <p>{results[ex.id]}</p>
                                        </div>
                                    );
                                })
                        }
                    </div>
                </div>
            </div>
        </div>
    );

}

export default ExerciseBoxes;